import { useCallback, useMemo, useRef, useState } from 'react';

import { useStableValue } from '@/hooks/useStableValue';
import { GraphQuery } from '@/types';

import { BulkActionPayload, BulkSelectionState } from './types';

export interface UseBulkSelectionParams {
  /** Current data loaded in the table (for manual mode key resolution) */
  data: Array<{ key: string }>;
  /** Active graph query/filters driving the table data */
  activeFilters: GraphQuery;
  /** Optional: additional value that, when changed, resets selection */
  filterVersion?: unknown;
}

export interface UseBulkSelectionReturn {
  // --- State ---
  state: BulkSelectionState;

  // --- Derived ---
  /** True if any items are selected (manual or all mode) */
  hasSelection: boolean;
  /** Display count: manual=keys.length, all mode shows items minus exclusions */
  selectedCount: number;
  /** True if every loaded row is selected (triggers banner) */
  isAllLoadedSelected: boolean;
  /** True if in 'all' mode */
  isSelectAll: boolean;

  // --- Actions ---
  /** Set manual selection keys (for Table.tsx selection.onChange) */
  setManualKeys: (keys: string[]) => void;
  /** Toggle individual key. In 'all' mode, adds/removes from excludedKeys */
  toggleKey: (key: string) => void;
  /** Select all loaded rows (step 1 of Gmail pattern) */
  selectAllLoaded: () => void;
  /** Promote to 'all' mode (step 2 of Gmail pattern — "Select all matching") */
  selectAllMatching: () => void;
  /** Clear all selection, return to manual mode with empty keys */
  clearSelection: () => void;
  /** Check if a specific key is selected */
  isSelected: (key: string) => boolean;

  // --- For API calls ---
  /** Build the selection payload for the bulk action endpoint */
  toBulkPayload: () => BulkActionPayload['selection'];

  // --- Legacy compatibility ---
  /** String array for Table.tsx selection.value prop */
  selectedRows: string[];
  /** Setter for Table.tsx selection.onChange prop */
  setSelectedRows: (keys: string[]) => void;
}

export function useBulkSelection({
  data,
  activeFilters,
  filterVersion,
}: UseBulkSelectionParams): UseBulkSelectionReturn {
  const [state, setState] = useState<BulkSelectionState>({
    mode: 'manual',
    keys: [],
  });

  // Use useStableValue for deep comparison of filters — not JSON.stringify
  const stableFilters = useStableValue(activeFilters);

  // Track previous stable filters to detect changes
  const prevFiltersRef = useRef(stableFilters);
  const prevFilterVersionRef = useRef(filterVersion);

  // Detect filter changes and reset selection
  // This runs synchronously during render (intentional — same pattern as useStableValue,
  // equivalent to React's getDerivedStateFromProps). The synchronous approach ensures
  // selection clears in the same render cycle as the filter change (no one-render delay).
  //
  // NOTE ON SEARCH BEHAVIOR: In the current codebase, search IS part of the filters
  // object passed as filterVersion. This means typing in a search box resets selection.
  // This is intentional: search changes the visible data set, so keeping selections of
  // now-invisible items would be confusing. If a future caller wants search to NOT reset
  // selection, they should exclude the search field from filterVersion.

  const filtersChanged =
    // eslint-disable-next-line react-hooks/refs
    prevFiltersRef.current !== stableFilters ||
    // eslint-disable-next-line react-hooks/refs
    prevFilterVersionRef.current !== filterVersion;

  if (filtersChanged) {
    // eslint-disable-next-line react-hooks/refs
    prevFiltersRef.current = stableFilters;
    // eslint-disable-next-line react-hooks/refs
    prevFilterVersionRef.current = filterVersion;
    // Reset selection when filters change
    setState({ mode: 'manual', keys: [] });
  }

  // Prune stale manual selections after a bulk-action mutation refetch.
  // When a mutated row drops out of the filtered view, the selected-key state
  // becomes stale. We only prune keys that are no longer present in the loaded
  // data — we never wipe the whole selection (preserves pagination / load-more).
  // Skipped when filtersChanged because that block already resets to empty.
  if (!filtersChanged && state.mode === 'manual' && state.keys.length > 0) {
    const loadedKeySet = new Set(data.map(d => d.key));
    const prunedKeys = state.keys.filter(k => loadedKeySet.has(k));
    if (prunedKeys.length !== state.keys.length) {
      setState({ mode: 'manual', keys: prunedKeys });
    }
  }

  // Derived values
  const loadedKeys = useMemo(() => data.map(d => d.key), [data]);

  const isAllLoadedSelected = useMemo(() => {
    if (state.mode === 'all') return true;
    if (state.mode === 'manual') {
      if (loadedKeys.length === 0) return false;
      return loadedKeys.every(k => state.keys.includes(k));
    }
    return false;
  }, [state, loadedKeys]);

  const isSelectAll = state.mode === 'all';

  const selectedCount = useMemo(() => {
    if (state.mode === 'manual') return state.keys.length;
    // In 'all' mode: loaded items minus excluded (guarded against negative)
    return Math.max(0, loadedKeys.length - state.excludedKeys.length);
  }, [state, loadedKeys]);

  // Note: hasSelection is false when selectedCount is 0, even in 'all' mode when all
  // loaded rows are excluded. This prevents submitting an empty bulk job.
  const hasSelection = selectedCount > 0;

  const isSelected = useCallback(
    (key: string): boolean => {
      if (state.mode === 'manual') return state.keys.includes(key);
      return !state.excludedKeys.includes(key);
    },
    [state]
  );

  const setManualKeys = useCallback((keys: string[]) => {
    setState({ mode: 'manual', keys });
  }, []);

  const toggleKey = useCallback((key: string) => {
    setState(prev => {
      if (prev.mode === 'manual') {
        if (prev.keys.includes(key)) {
          return { mode: 'manual', keys: prev.keys.filter(k => k !== key) };
        }
        return { mode: 'manual', keys: [...prev.keys, key] };
      }
      // 'all' mode — manage excludedKeys
      if (prev.excludedKeys.includes(key)) {
        return {
          ...prev,
          excludedKeys: prev.excludedKeys.filter(k => k !== key),
        };
      }
      return { ...prev, excludedKeys: [...prev.excludedKeys, key] };
    });
  }, []);

  const selectAllLoaded = useCallback(() => {
    setState({ mode: 'manual', keys: loadedKeys });
  }, [loadedKeys]);

  const selectAllMatching = useCallback(() => {
    setState({
      mode: 'all',
      filters: stableFilters,
      excludedKeys: [],
    });
  }, [stableFilters]);

  const clearSelection = useCallback(() => {
    setState({ mode: 'manual', keys: [] });
  }, []);

  /**
   * toBulkPayload maps the internal selection state to the backend API contract.
   * Internal 'manual' → backend 'explicit'
   * Internal 'all'    → backend 'query'
   */
  const toBulkPayload = useCallback((): BulkActionPayload['selection'] => {
    if (state.mode === 'manual') {
      return { mode: 'explicit', keys: state.keys };
    }
    return {
      mode: 'query',
      query: state.filters,
      excludeKeys: state.excludedKeys,
    };
  }, [state]);

  // Legacy compatibility: selectedRows maps to string[]
  const selectedRows = useMemo(() => {
    if (state.mode === 'manual') return state.keys;
    // In 'all' mode: return all loaded keys minus excluded ones
    return loadedKeys.filter(k => !state.excludedKeys.includes(k));
  }, [state, loadedKeys]);

  const setSelectedRows = useCallback((keys: string[]) => {
    setState({ mode: 'manual', keys });
  }, []);

  return {
    state,
    hasSelection,
    selectedCount,
    isAllLoadedSelected,
    isSelectAll,
    setManualKeys,
    toggleKey,
    selectAllLoaded,
    selectAllMatching,
    clearSelection,
    isSelected,
    toBulkPayload,
    selectedRows,
    setSelectedRows,
  };
}
